import type { HTMLAttributes, ReactNode } from 'react';
import { common } from '@featit/shared/i18n/messages/en/common';
import { registerEnglishArea } from '@/i18n/catalogue';
import { useT } from '@/i18n/LocaleProvider';
import { LoadStage } from './LoadStage';

registerEnglishArea('common', common);

/**
 * F-0604 WAIT-19 to WAIT-23 (`docs/concept-waiting-motion.md`): a pane, panel or list that is waiting,
 * drawn through the shared stages. It is rendered only while it waits, so its mounting is the wait's
 * start: the bars are the live skeleton from 300 ms (`.evx-skeleton`'s own hold), the session's study
 * with *Loading …* from 2 s, and from the cold-stall deadline the shared failure with *Try again*.
 *
 * The element it draws is the `div` the site used to draw (`className`, `data-testid` and the rest pass
 * through), with `aria-busy` and the *Loading …* label it announced. `onRetry` is the site's own read;
 * without one, *Try again* reads the screen again.
 */
export interface IStagedPlaceholderProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * What is loading, as the site announced it: *Loading the webhooks*. As `aria-label` the placeholder
   * announces it itself; as `label` it only names the wait (the caption and the failure) and the child
   * keeps its own announcement.
   */
  'aria-label'?: string;
  label?: string;
  /** The site's own read, again. Default: the screen is loaded again. */
  onRetry?: () => void;
  /** `false` where the screen's own deadline owns the failure. Default `true`. */
  deadline?: boolean;
  children: ReactNode;
}

const reloadScreen = () => window.location.reload();

/**
 * What a failed pane names: its label less its *Loading* words where they frame it exactly
 * ("Loading the views" → "The views", "Ansichten werden geladen" → "Ansichten"), the label as given
 * where they do not, and nothing for a pane with no label of its own, which says only that loading
 * failed. Its first letter is capitalised.
 */
export function failedSubject(label: string | undefined): string | null {
  const words = label?.replace(/…$/, '').trimEnd();
  if (!words) return null;
  const subject =
    /^Loading (.+)$/.exec(words)?.[1] ?? /^(.+) (?:wird|werden) geladen$/.exec(words)?.[1] ?? words;
  return `${subject.charAt(0).toUpperCase()}${subject.slice(1)}`;
}

export function StagedPlaceholder({
  onRetry,
  deadline = true,
  children,
  'aria-label': announced,
  label: named,
  ...divProps
}: IStagedPlaceholderProps) {
  const t = useT();
  const own = (announced ?? named) as string | undefined;
  const label = own ?? t('common.waiting.loading');
  const subject = failedSubject(own);
  return (
    <LoadStage
      waiting
      stageKey={label}
      // F-0621: the site's words, closed the way the reader's language closes them ("…" / " …").
      caption={t('common.waiting.caption', { what: label.replace(/…$/, '').trimEnd() })}
      failureTitle={
        subject ? t('common.waiting.failed', { what: subject }) : t('common.waiting.failedBare')
      }
      onRetry={onRetry ?? reloadScreen}
      deadline={deadline}
      placeholder={
        // WAIT-11: a wait with a label is announced once, here: the placeholder is the status and its label
        // is the name. A caller adds no `role="status"` of its own, and nests none inside (`StagedPlaceholder.role.guard.test.ts`).
        <div
          {...divProps}
          role={announced ? 'status' : undefined}
          aria-busy="true"
          aria-label={announced}
        >
          {children}
        </div>
      }
    />
  );
}
